import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { create } from 'zustand';
import { StyledInput } from '~/components/ui';
import { useDebounce } from '../../../hooks';
import useAuth from '../../../hooks/useAuth';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useClubSlug } from '../../Clubs/hooks';
import { ClubMember } from '../types';

interface ClubMemberAuthStore {
    clubMemberAuthToken: string;
    setClubMemberAuthToken: (birthday: string) => void;
}

export const useClubMemberAuthStore = create<ClubMemberAuthStore>((set) => ({
    clubMemberAuthToken: '',
    setClubMemberAuthToken: (clubMemberAuthToken) => set((store) => ({ ...store, clubMemberAuthToken }))
}));

interface Props {
    email?: string;
    clubSlug?: string;
}

type AuthorizationMode = 'PIN' | 'BIRTHDAY';
type AuthorizationResponse = {
    member: Pick<ClubMember, 'name' | 'id'>;
} & {
    mode: AuthorizationMode | 'NONE';
    options: string[] | null;
};

const ClubMemberAuthorization = ({ email, clubSlug: _clubSlug }: Props) => {
    const { t } = useTranslation('member-management');
    const { clubMemberAuthToken, setClubMemberAuthToken } = useClubMemberAuthStore();
    const clubSlug = useClubSlug(_clubSlug);
    const { user } = useAuth();
    const debouncedEmail = useDebouncedValue(email ?? user?.email, 500);
    const [pin, setPin] = useState<string>(clubMemberAuthToken);
    const setDebouncedPin = useDebounce(setClubMemberAuthToken, 500);

    const { data, isLoading } = useSWR<AuthorizationResponse>(
        debouncedEmail ? `club-member/${clubSlug}/auth-mode?email=${debouncedEmail}` : null
    );

    if (isLoading || !data) return null;
    if (data.mode === 'BIRTHDAY')
        return (
            <>
                <label>{t('clubMember.authorization.selectBirthday')}</label>
                <div className="tw-mb-4 tw-grid tw-grid-cols-3 tw-gap-4">
                    {data.options?.map((option, index) => (
                        <button
                            className={cx(
                                clubMemberAuthToken !== option ? 'btn-outline-secondary' : 'btn-secondary',
                                'btn btn-block !tw-mt-0'
                            )}
                            type="button"
                            onClick={() => setClubMemberAuthToken(option)}
                            key={index}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </>
        );

    if (data.mode === 'PIN')
        return (
            <StyledInput
                label={t('clubMember.authorization.pinLabel')}
                name="email"
                type="password"
                value={pin}
                onChange={(e) => {
                    const value = e.target.value;
                    setPin(value);
                    setDebouncedPin(value);
                }}
            />
        );

    return null;
};

export default ClubMemberAuthorization;
